import React, { useCallback, useState } from 'react'
import { EdgeLabelRenderer, EdgeProps, getBezierPath, useReactFlow } from '@xyflow/react'

import { IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { LAYOUT, NODE_STYLES } from './constants'
import { WorkflowManager, WorkflowNodeManager } from './managers'
import { WorkflowEdge, WorkflowNode } from './types'

export const WorkflowEdgeComponent = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  source,
  target,
}: EdgeProps) => {
  const { setEdges, getEdges, getNodes, setNodes } = useReactFlow<WorkflowNode, WorkflowEdge>()
  const nodes = getNodes()
  const currentNode = nodes.find((node) => node.id === source)
  const [isHovered, setIsHovered] = useState(false)
  const currentEdge = getEdges().find((edge) => edge.id === id)

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const onDeleteEdge = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setEdges((edges) => edges.filter((edge) => edge.id !== id))

      if (currentNode?.data.actionValue?.type === IActionType.ConditionalSwitch) {
        const newNodes = WorkflowManager.removeActionIdFromSwitchActionCase(nodes, source, target)
        setNodes(newNodes)
      }

      // Trigger auto-save
      const event = new CustomEvent('triggerAutoSave')
      window.dispatchEvent(event)
    },
    [currentNode?.data.actionValue, id, nodes, setEdges, setNodes, source, target]
  )

  const onAddNodeBetween = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      // Extract branch information from sourceHandle if it exists
      let branch: string | null = null
      if (currentEdge?.sourceHandle) {
        branch = WorkflowNodeManager.extractBranchFromSourceHandle(currentEdge.sourceHandle)
      }

      const eventDetail = {
        sourceNodeId: source,
        insertBetween: true,
        edgeId: id,
        edgeInfo: { sourceX, sourceY, targetX, targetY },
        branch,
      }
      const event = new CustomEvent('openNodePalette', { detail: eventDetail })
      window.dispatchEvent(event)
    },
    [currentEdge?.sourceHandle, source, id, sourceX, sourceY, targetX, targetY]
  )

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])
  return (
    <>
      {/* Invisible wider path for larger hover area */}
      <path
        d={edgePath}
        stroke='transparent'
        strokeWidth={LAYOUT.edgeHoverWidth}
        fill='none'
        className='cursor-pointer'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {/* Visible edge path */}
      <path
        id={id}
        className='react-flow__edge-path'
        d={edgePath}
        stroke={isHovered ? NODE_STYLES.edge.hovered.stroke : NODE_STYLES.edge.default.stroke}
        strokeWidth={NODE_STYLES.edge.default.strokeWidth}
        fill='none'
        style={{
          stroke: isHovered ? NODE_STYLES.edge.hovered.stroke : NODE_STYLES.edge.default.stroke,
          strokeWidth: isHovered ? NODE_STYLES.edge.hovered.strokeWidth : NODE_STYLES.edge.default.strokeWidth,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />

      {/* Edge controls - show on hover */}
      <EdgeLabelRenderer>
        <div
          className={`nodrag nopan transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className='flex gap-1'>
            <button className={NODE_STYLES.edgeControls.addButton} onClick={onAddNodeBetween} title='Add node'>
              <MaterialIcon size={16} icon='add' className='text-teal-500' />
            </button>
            <button className={NODE_STYLES.edgeControls.deleteButton} onClick={onDeleteEdge} title='Delete edge'>
              <MaterialIcon size={16} icon='delete' className='text-red-500' />
            </button>
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
